<template>
  <div class="unanswered-container p-4">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
          {{ t('unansweredQuestions.title') }}
        </h1>
        <p class="text text-gray-600 dark:text-gray-400">
          {{ t('unansweredQuestions.subtitle') }}
        </p>
      </div>
       <button class="btn btn-sm btn-outline" @click="fetchUnansweredQuestions" :disabled="isLoading === true">
          <Icon icon="tabler:refresh" :class="{'animate-spin': isLoading === true}" class="mr-1"/>
          {{ t('unansweredQuestions.refresh') }}
       </button>
    </div>


    <div class="filters-card card shadow bg-white dark:bg-gray-800 w-full rounded-md p-4 border dark:border-gray-700 flex mb-6" style="border-radius: 10px;">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 w-full items-end search-fields-grid">
        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('unansweredQuestions.agentLabel') }}</span>
          </label>
          <MultiSelect
            v-model="selectedAgentIds"
            :options="availableAgentsForFilter"
            :placeholder="t('unansweredQuestions.allAgentsPlaceholder')"
            id-field="agentid"
            label-field="agentname"
            :is-loading="availableAgentsForFilter.length === 0"
            :multiple="true"
            :allow-empty="true"
            :close-on-select="false"
          />
        </div>

        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('unansweredQuestions.dateRangeLabel') }}</span>
          </label>
          <CustomDateRangePicker 
            v-model="dateRange" 
            :clearable="false" 
            :placeholder="t('unansweredQuestions.dateRangePlaceholder')" 
            :auto-apply="true"
          />
        </div>
        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('unansweredQuestions.quickSelectLabel') }}</span>
          </label>
          <select v-model="selectedPreset" class="select select-bordered w-full" @change="applyPreset">
            <option value="">{{ t('unansweredQuestions.customRangeOption') }}</option>
            <option v-for="(label, key) in presets" :key="key" :value="key">
              {{ t(`reports.presets.${key}`) }}
            </option>
          </select>
        </div>

        <div class="form-control">
          <button class="btn w-full" @click="fetchUnansweredQuestions" :disabled="isLoading === true"> 
            <Icon icon="tabler:filter" class="mr-1" />
            {{ t('unansweredQuestions.applyFilters') }}
          </button>
        </div>

      
        
      </div>
    </div>

    <div class="results-card card shadow bg-white dark:bg-gray-800 w-full rounded-md p-4 border dark:border-gray-700 flex flex-col" style="border-radius: 10px; min-height: 70vh;">
       <div class="flex justify-between items-center mb-4 w-full" :class="{ 'flex-row-reverse': isRTL === true }">
         <div class="flex items-center table-buttons-row">
           <button 
             class="btn btn-warning"
             @click="isDiscardModalOpen = true"
             :disabled="selectedItems.length === 0">
             <Icon icon="tabler:trash-x" class="mr-1" />
             {{ t('unansweredQuestions.discardSelected', { count: selectedItems.length }) }}
           </button>
           <div class="form-control" style="width: fit-content;">
            <input 
              type="text" 
              v-model="searchValue" 
              :placeholder="t('unansweredQuestions.searchPlaceholder')" 
                class="input input-bordered w-full max-w-xs"
            />
            </div>
          </div>
         <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100" :class="isRTL === true ? 'text-right' : 'text-left'" :dir="isRTL === true ? 'rtl' : 'ltr'">{{ t('unansweredQuestions.questionsListTitle') }}</h2>
       </div>
      <div class="overflow-auto flex-1 w-full">
        <ThemeOnly theme="modern" :invert="true">
          <EasyDataTable
            :headers="headers"
          :items="unansweredQuestionsList"
          :loading="isLoading === true"
          :search-value="searchValue"
          :search-field="['question', 'agentNames']"
          table-class-name="enterprise-table"
          buttons-pagination
          v-model:items-selected="selectedItems"
          :rows-items="[10, 25, 50]"
          :rows-per-page="10"
          :empty-message="t('unansweredQuestions.noQuestionsFound')"
        >
          <template #item-question="{ question }">
            <div class="max-w-xl whitespace-normal">{{ question }}</div>
          </template>
          <template #item-occurredOn="item">
             <div>{{ formatDateStandard(item.firstOccurred) }}</div>
          </template>
          <template #item-agentNames="{ agentNames }">
             <div class="max-w-xs truncate" :title="agentNames">{{ agentNames }}</div>
          </template>
          <template #item-actions="item">
            <div class="flex table-buttons-row">
                <button 
                    class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-2" 
                    @click="openAnswerModal(item)">
                  <Icon icon="tabler:message-plus" class="mr-1" />
                  {{ t('unansweredQuestions.answerButton') }}
                </button>
                <button 
                    class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" 
                    @click="openConversationModal(item)"
                    :disabled="item === null || item === undefined || item.representativeSessionId === null || item.representativeSessionId === undefined || (typeof item.representativeSessionId === 'string' && item.representativeSessionId.trim() === '') || item.representativeConversationId === null || item.representativeConversationId === undefined || (typeof item.representativeConversationId === 'string' && item.representativeConversationId.trim() === '')">
                  <Icon icon="tabler:message-2" class="mr-1" />
                  {{ t('unansweredQuestions.conversationButton') }}
                </button>
             </div>
          </template>
          <template #loading>
            <div class="flex justify-center items-center p-8">
              <ModernLoader />
            </div>
          </template>
        </EasyDataTable>
        </ThemeOnly>
      </div>
    </div>


    <dialog ref="answerDialogRef" class="modal modal-middle">
      <div class="modal-box">
        <h3 class="font-bold text-lg mb-4">{{ t('unansweredQuestions.provideAnswerModalTitle') }}</h3>
        <div v-if="selectedQuestion !== null && selectedQuestion !== undefined" class="space-y-4">
          <div>
            <label class="label">
              <span class="label-text font-semibold">{{ t('unansweredQuestions.questionLabel') }}</span>
            </label>
            <p class="p-2 bg-base-200 rounded-md text-base">{{ selectedQuestion.question }}</p>
          </div>
          <div class="form-control">
            <label class="label">
              <span class="label-text font-semibold">{{ t('unansweredQuestions.yourAnswerLabel') }}</span>
               <span class="label-text-alt text-error" v-if="newAnswer === ''">{{ t('unansweredQuestions.requiredLabel') }}</span>
            </label>
            <textarea 
                v-model="newAnswer"
                :placeholder="t('unansweredQuestions.answerPlaceholder')" 
                class="textarea textarea-bordered h-32"
                required
            ></textarea>
          </div>
        </div>
         <div v-else class="text-center text-gray-500 py-4">{{ t('unansweredQuestions.selectQuestionFirst') }}</div>

        <div class="modal-action">
          <div class="flex table-buttons-row">
            <button type="button" class="btn" @click="closeAnswerModal">{{ t('common.cancel') }}</button>
            <button 
              type="button" 
              class="btn" style="color:#fff !important;" 
              @click="submitAnswer"
              :disabled="newAnswer === '' || isSubmitting === true"
            >
               <span v-if="isSubmitting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('unansweredQuestions.addToKnowledgeBaseButton') }}
            </button>
          </div>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button @click="closeAnswerModal">close</button>
      </form>
    </dialog>

     <AppModal :visible="isConversationModalOpen === true" boxClass="max-w-7xl w-11/12" @close="closeConversationModal">
        <template #left>
            <div class="flex items-center table-buttons-row">
              <button id="export-pdf-btn" class="btn btn-sm btn-outline btn-info" @click="exportConversationToPdf" :disabled="conversationHistory.length === 0">
                <Icon icon="tabler:file-type-pdf" class="mr-1" /> {{ t('reports.conversationModal.exportPdfButton') }}
              </button>
              <button id="export-txt-btn" class="btn btn-sm btn-outline btn-neutral" @click="exportConversationToTxt" :disabled="conversationHistory.length === 0">
                <Icon icon="tabler:file-type-txt" class="mr-1" /> {{ t('reports.conversationModal.exportTxtButton') }}
              </button>
            </div>
        </template>
        <template #header>
            <h3 class="font-bold text-lg">
              {{ t('unansweredQuestions.conversationModalTitle') }}
               <template v-if="conversationAnalysisData !== null && conversationAnalysisData !== undefined">
                <span v-if="conversationAnalysisData.conversationsubject !== undefined && conversationAnalysisData.conversationsubject !== null && conversationAnalysisData.conversationsubject !== ''">
                  {{ conversationAnalysisData.conversationsubject }} ({{ selectedSessionId }})
                </span>
                <span v-else>
                   {{ selectedSessionId }}
                </span>
              </template>
              <template v-else>
                 {{ isDefinedAndNotEmpty(selectedSessionId) != false ? selectedSessionId : t('unansweredQuestions.loading') }}
              </template>
            </h3>
        </template>

        <div :class="shouldRenderAnalysisColumn === true ? 'grid grid-cols-3 grid-space-4' : 'block'">
            <div v-if="shouldRenderAnalysisColumn === true" :class="shouldShowAnalysisDetails === true ? 'col-span-1 p-4 border-r dark:border-gray-700 max-h-[70vh] overflow-y-auto bg-base-200 rounded mr-4' : 'col-span-1 p-4 border-r dark:border-gray-700 max-h-[70vh] overflow-y-auto bg-base-200 rounded mr-4'">
                <h4 class="font-semibold text-lg mb-4 sticky top-0 bg-base-200 py-2 border-b dark:border-gray-600">{{ t('unansweredQuestions.analysisDetailsTitle') }}</h4>
                <div v-if="isLoadingConversation === true" class="text-base skeleton-shell">
                    <div class="h-4 w-36 bg-base-300 rounded mb-3"></div>
                    <div class="h-16 bg-base-300 rounded mb-3"></div>
                    <div class="h-10 bg-base-300 rounded mb-3"></div>
                    <div class="h-10 bg-base-300 rounded mb-3"></div>
                    <div class="h-24 bg-base-300 rounded"></div>
                </div>
                <div v-else-if="shouldShowAnalysisDetails === true" class="space-y-4 text-base">
                   
                          <div class="space-y-2">
                            <p class="flex items-start"><Icon icon="tabler:message" class="mr-2 mt-1 text-gray-500"/><strong>{{ t('unansweredQuestions.subjectLabel') }}</strong><span class="ml-2">{{ conversationAnalysisData.conversationsubject }}</span></p>
                              <p class="flex items-center"><Icon icon="tabler:language" class="mr-2 text-gray-500"/><strong>{{ t('unansweredQuestions.languageLabel') }}</strong><span class="ml-2">{{ conversationAnalysisData.languagedetected }}</span></p>
                              <div class="bg-gray-100 dark:bg-gray-700 rounded-md p-2">
                                 <p class="flex items-start"><Icon icon="tabler:article" class="mr-2 mt-1 text-gray-500 shrink-0"/><strong>{{ t('unansweredQuestions.summaryLabel') }}</strong></p>
                                 <p class="ml-7">{{ conversationAnalysisData.summary }}</p>
                              </div>
                            
                              <p class="flex items-center"><Icon icon="tabler:tag" class="mr-2 text-gray-500"/><strong>{{ t('unansweredQuestions.typeLabel') }}</strong><span class="ml-2 badge badge-ghost badge-sm">{{ conversationAnalysisData.conversationtype }}</span></p>
                              <p class="flex items-center"><Icon icon="tabler:circle-check" class="mr-2 text-gray-500"/><strong>{{ t('unansweredQuestions.crsLabel') }}</strong>
                                 <span :class="['ml-2 badge badge-sm', 
                                              conversationAnalysisData.crs === 'True' ? 'badge-success' : 
                                              conversationAnalysisData.crs === 'False' ? 'badge-error' : 'badge-neutral']">
                                   {{ conversationAnalysisData.crs === 'True' ? t('reports.boolean.yes') : t('reports.boolean.no') }}
                                 </span>
                              </p>
                              <p class="flex items-center"><Icon icon="tabler:percentage" class="mr-2 text-gray-500"/><strong>{{ t('unansweredQuestions.predictedCsatLabel') }}</strong><span class="ml-2">{{ conversationAnalysisData.nps }}</span></p>
                              <p class="flex items-center"><Icon icon="tabler:git-branch" class="mr-2 text-gray-500"/><strong>{{ t('unansweredQuestions.followUpLabel') }}</strong>
                                 <span :class="['ml-2 badge badge-sm', conversationAnalysisData.isconversationfollowup === true ? 'badge-info' : 'badge-ghost']">
                                   {{ conversationAnalysisData.isconversationfollowup === true ? t('reports.boolean.yes') : t('reports.boolean.no') }}
                                 </span>
                              </p>
                               <p class="flex items-center"><Icon icon="tabler:mood-angry" class="mr-2 text-gray-500"/><strong>{{ t('unansweredQuestions.cursesDetectedLabel') }}</strong>
                                 <span :class="['ml-2 badge badge-sm', conversationAnalysisData.cursesdetected === true ? 'badge-warning' : 'badge-ghost']">
                                  {{ conversationAnalysisData.cursesdetected === true ? t('reports.boolean.yes') : t('reports.boolean.no') }}
                                 </span>
                              </p>
                              <p v-if="conversationAnalysisData.cursesdetected === true" class="flex items-start pl-6"><strong class="mr-1">{{ t('unansweredQuestions.cursesFoundLabel') }}</strong><span class="text">{{ (conversationAnalysisData.cursesfound != undefined && conversationAnalysisData.cursesfound != null && conversationAnalysisData.cursesfound.length > 0 ? conversationAnalysisData.cursesfound : []).join(', ') }}</span></p>
                              <p class="flex items-center"><Icon icon="tabler:logout" class="mr-2 text-gray-500"/><strong>{{ t('unansweredQuestions.chatAbandonedLabel') }}</strong>
                                <span :class="['ml-2 badge badge-sm', conversationAnalysisData.ischatabandoned === true ? 'badge-warning' : 'badge-ghost']">
                                  {{ conversationAnalysisData.ischatabandoned === true ? t('reports.boolean.yes') : t('reports.boolean.no') }}
                                </span>
                              </p>
                          </div>
                          
                         
                          <div v-if="conversationTags !== null && conversationTags !== undefined && conversationTags.length > 0" class="mt-4 pt-4 border-t dark:border-gray-600 space-y-2">
                            <h5 class="font-semibold text-base mb-2 flex items-center"><Icon icon="tabler:tags" class="mr-2 text-info"/>{{ t('unansweredQuestions.identifiedTagsTitle') }}</h5>
                            <div class="flex flex-wrap space-x-2 pl-6">
                                <span v-for="tag in conversationTags" :key="tag" class="badge badge-info badge-outline">{{ tag }}</span>
                            </div>
                         </div>

        
                          <div class="mt-4 pt-4 border-t dark:border-gray-600 space-y-3">
                            <h5 class="font-semibold text-base mb-2 flex items-center"><Icon icon="tabler:chart-pie" class="mr-2 text-primary"/>{{ t('unansweredQuestions.overallSentimentTitle') }}
                               <span :class="['ml-2 badge', getSentimentBadgeClass(conversationAnalysisData.overallsentiment)]"> 
                                  {{ conversationAnalysisData.overallsentiment }}
                               </span>
                            </h5>
                    
                          </div>
                          <div v-if="conversationAnalysisData.participantsentiments !== null && conversationAnalysisData.participantsentiments !== undefined && conversationAnalysisData.participantsentiments.usersentiment !== null && conversationAnalysisData.participantsentiments.usersentiment !== undefined" class="mt-3 pt-3 border-t dark:border-gray-600 space-y-1">
                             <h5 class="font-semibold mb-1 flex items-center"><Icon icon="tabler:user" class="mr-2"/>{{ t('unansweredQuestions.userSentimentTitle') }}
                                <span :class="['ml-2 badge badge-sm', getSentimentBadgeClass(conversationAnalysisData.participantsentiments.usersentiment.sentiment)]">
                                   {{ conversationAnalysisData.participantsentiments.usersentiment.sentiment }}
                                </span>
                             </h5>
                  
                          </div>
                           <div v-if="conversationAnalysisData.participantsentiments !== null && conversationAnalysisData.participantsentiments !== undefined && conversationAnalysisData.participantsentiments.agentsentiment !== null && conversationAnalysisData.participantsentiments.agentsentiment !== undefined" class="mt-3 pt-3 border-t dark:border-gray-600 space-y-1">
                             <h5 class="font-semibold mb-1 flex items-center"><Icon icon="tabler:headset" class="mr-2"/>{{ t('unansweredQuestions.agentSentimentTitle') }}
                                 <span :class="['ml-2 badge badge-sm', getSentimentBadgeClass(conversationAnalysisData.participantsentiments.agentsentiment.sentiment)]">
                                   {{ conversationAnalysisData.participantsentiments.agentsentiment.sentiment }}
                                 </span>
                             </h5>
                   
                          </div>
                          
             
                          <div v-if="conversationAnalysisData.unansweredquestions !== null && conversationAnalysisData.unansweredquestions !== undefined && conversationAnalysisData.unansweredquestions.length > 0" class="mt-3 pt-3 border-t dark:border-gray-600">
                            <h5 class="font-semibold mb-2 flex items-center"><Icon icon="tabler:help-circle" class="mr-2 text-warning"/>{{ t('unansweredQuestions.unansweredInConvoTitle') }}</h5>
                            <ul class="list-disc list-inside space-y-2 pl-6">
                              <li v-for="(item, idx) in conversationAnalysisData.unansweredquestions" :key="idx">
                                <strong>{{ t('unansweredQuestions.questionPrefix') }}</strong> {{ item.question }} <br/> <span class="italic text-gray-500"><strong>{{ t('unansweredQuestions.answerPrefix') }}</strong> {{ item.answer }}</span>
                              </li>
                            </ul>
                          </div>
                      </div>
                  </div>
                  <div :class="shouldRenderAnalysisColumn === true ? 'col-span-2' : 'col-span-3'">
                      <div ref="conversationContainerRef" class="py-4 max-h-[70vh] overflow-y-auto bg-base-200 rounded p-4">
                          <div v-if="isLoadingConversation === true" class="p-6 skeleton-shell">
                              <div class="chat chat-start mb-4">
                                  <div class="chat-header text-xs opacity-50 mb-1">
                                      <div class="h-3 w-16 bg-base-300 rounded"></div>
                                  </div>
                                  <div class="chat-bubble">
                                      <div class="h-4 w-44 bg-base-300 rounded mb-2"></div>
                                      <div class="h-4 w-32 bg-base-300 rounded"></div>
                                  </div>
                              </div>
                              <div class="chat chat-end mb-4">
                                  <div class="chat-header text-xs opacity-50 mb-1">
                                      <div class="h-3 w-20 bg-base-300 rounded"></div>
                                  </div>
                                  <div class="chat-bubble chat-bubble-primary">
                                      <div class="h-4 w-40 bg-base-300 rounded mb-2"></div>
                                      <div class="h-4 w-28 bg-base-300 rounded"></div>
                                  </div>
                              </div>
                              <div class="chat chat-start">
                                  <div class="chat-header text-xs opacity-50 mb-1">
                                      <div class="h-3 w-16 bg-base-300 rounded"></div>
                                  </div>
                                  <div class="chat-bubble">
                                      <div class="h-4 w-52 bg-base-300 rounded"></div>
                                  </div>
                              </div>
                          </div>
                          <div v-else-if="conversationHistory !== null && conversationHistory !== undefined && conversationHistory.length > 0" class="space-y-4">
                              <template v-for="(turn, index) in conversationHistory" :key="index">
                                  <div class="chat chat-end" v-if="turn !== null && turn !== undefined && hasConversationText(turn.userquery) === true" :style="{ direction: getTextDirection(turn.userquery) }">
                                      <div class="chat-header text-xs opacity-50 mb-1">
                                         {{ t('unansweredQuestions.userChatHeader') }}
                                         <time class="text-xs opacity-50 ml-1">{{ formatDateHardcoded(turn.createdat) }}</time>
                                      </div>
                                      <div class="chat-bubble chat-bubble-primary" v-html="escapeHtml(turn.userquery)"></div>
                                  </div>
                                   <div class="chat chat-start" v-if="turn !== null && turn !== undefined && hasConversationText(turn.response) === true" :style="{ direction: getTextDirection(turn.response) }">
                                       <div class="chat-header text-xs opacity-50 mb-1">
                                          {{ t('unansweredQuestions.agentChatHeader') }}
                                          <time class="text-xs opacity-50 ml-1">{{ formatDateHardcoded(turn.createdat) }}</time>
                                       </div>
                                       <div class="chat-bubble" v-html="sanitizeHtml(turn.response)"></div>
                                   </div>
                              </template>
                          </div>
                          <div v-else class="text-center p-6">
                              <p>{{ t('unansweredQuestions.noConversationHistory') }}</p>
                          </div>
                      </div>
                   </div>
               </div>
      </AppModal>

      <it-modal v-model="isDiscardModalOpen">
        <div class="p-4">
          <h2 class="text-xl font-bold mb-4">{{ t('unansweredQuestions.discardModalTitle') }}</h2>
          <p class="mb-6">{{ t('unansweredQuestions.discardModalText', { count: selectedItems.length }) }}</p>
          <div class="flex justify-end mt-6">
            <button @click="isDiscardModalOpen=false" type="button" class="btn btn-ghost mr-3">
              {{ t('common.cancel') }}
            </button>
            <button @click="discardSelectedQuestions" class="btn btn-error">
              <span v-if="isDiscarding === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('unansweredQuestions.discardButton') }}
            </button>
          </div>
        </div>
      </it-modal>

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import { Icon } from '@iconify/vue/dist/offline';
import router from '@/router';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import CustomDateRangePicker from '@/components/CustomDateRangePicker.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import AppModal from '@/components/AppModal.vue';
import { useI18n } from '@/i18n';
import isDefinedAndNotEmpty from '../../isDefinedAndNotEmpty.js';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas-pro';
import ModernLoader from '@/components/ModernLoader.vue';
import { getAllAIChats } from '@/shared/services/aiChatsService';
import ThemeOnly from '@/components/ThemeOnly.vue';
import { formatDateTimeForViewer } from '@/shared/utils/dateTime';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';

const { t, currentLanguage } = useI18n();
const isRTL = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');
const toast = useToast();
const unansweredQuestionsList = ref([]);
const isLoading = ref(false);
const searchValue = ref('');

const dateRange = ref([]);
const availableAgentsForFilter = ref([]);
const selectedAgentIds = ref([]);
const selectedPreset = ref('');

const answerDialogRef = ref(null);
const selectedQuestion = ref(null);
const newAnswer = ref('');
const isSubmitting = ref(false);

const conversationModalRef = ref(null);
const selectedSessionId = ref(null);
const selectedConversationId = ref(null);
const conversationHistory = ref([]);
const isLoadingConversation = ref(false);
const conversationAnalysisData = ref(null);
const conversationTags = ref([]); 
const shouldShowAnalysisDetails = computed(() => {
  const data = conversationAnalysisData.value;
  if (data === null || data === undefined) {
    return false;
  }
  const candidates = [
    data.conversationsubject,
    data.languagedetected,
    data.summary,
    data.conversationtype,
    data.crs,
    data.nps,
    data.cursesdetected,
    data.cursesfound,
    data.isconversationfollowup,
    data.ischatabandoned,
    data.overallsentiment,
    data.overallsentimentscores,
    data.participantsentiments,
    data.unansweredquestions,
    data.identifiedtags,
    data.analysisengine
  ];
  for (let index = 0; index < candidates.length; index = index + 1) {
    const value = candidates[index];
    if (value === null || value === undefined) {
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') {
      continue;
    }
    if (Array.isArray(value) === true && value.length === 0) {
      continue;
    }
    if (typeof value === 'object' && Array.isArray(value) !== true && Object.keys(value).length === 0) {
      continue;
    }
    return true;
  }
  return false;
});
const shouldRenderAnalysisColumn = computed(() => isLoadingConversation.value === true || shouldShowAnalysisDetails.value === true);
const conversationContainerRef = ref(null);

const isConversationModalOpen = ref(false); 


const selectedItems = ref([]);
const isDiscardModalOpen = ref(false);
const isDiscarding = ref(false);

const headers = computed(() => [
  { text: t('unansweredQuestions.headers.question'), value: 'question', sortable: true },
  { text: t('unansweredQuestions.headers.occurredOn'), value: 'occurredOn', sortable: true }, 
  { text: t('unansweredQuestions.headers.agentInvolved'), value: 'agentNames', sortable: true },
  { text: t('unansweredQuestions.headers.sessionID'), value: 'sessionid', sortable: true },
  { text: t('unansweredQuestions.headers.tags'), value: 'tags', sortable: false },
  { text: t('unansweredQuestions.headers.actions'), value: 'actions' }
]);

const presets = {
  today: 'Today',
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month'
};

const presetRanges = {
  today: () => {
    const today = new Date();
    return [today, today];
  },
  thisWeek: () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    return [startOfWeek, today];
  },
  lastWeek: () => {
    const today = new Date();
    const startOfLastWeek = new Date(today);
    startOfLastWeek.setDate(today.getDate() - today.getDay() - 7);
    const endOfLastWeek = new Date(today);
    endOfLastWeek.setDate(today.getDate() - today.getDay() - 1);
    return [startOfLastWeek, endOfLastWeek];
  },
  thisMonth: () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return [startOfMonth, today];
  },
  lastMonth: () => {
    const today = new Date();
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    return [startOfLastMonth, endOfLastMonth];
  }
};

const applyPreset = () => {
  if (selectedPreset.value !== '' && presetRanges[selectedPreset.value] !== undefined) {
    dateRange.value = presetRanges[selectedPreset.value]();
  }
};

const getAuthToken = () => {
  const cookieString = document.cookie;
  if (cookieString === null || cookieString === undefined || cookieString === '') {
    return '';
  }
  const tokenRow = cookieString.split('; ').find(row => row.startsWith('authToken=') === true);
  if (tokenRow === undefined) {
    return '';
  }
  const token = tokenRow.split('=')[1];
  if (token === undefined || token === null) {
    return '';
  }
  return token;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleAuthError = (error) => {
  if (error !== null && error !== undefined && error.response !== null && error.response !== undefined) {
      if (error.response.status === 401 || error.response.status === 403) {
    router.push('/login');
      }
  }
};

const fetchUnansweredQuestions = async () => {
  isLoading.value = true;
  selectedItems.value = [];
  try {
    const params = {};
    if (dateRange.value !== false && dateRange.value.length === 2) {
      const startDate = dateRange.value[0];
      const endDate = dateRange.value[1];
      
      if (startDate !== null && startDate !== undefined && endDate !== null && endDate !== undefined) {
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        params.startDate = startDate.toISOString();
        params.endDate = endDate.toISOString();
      } else {
          console.warn('Date range contains null/undefined dates');
      }
    }
    if (selectedAgentIds.value.length > 0) {
      params.agentIds = selectedAgentIds.value;
    }

    const response = await axios.get(`${globalState.apiEndpoint}/api/unanswered/`, {
      headers: getAuthHeaders(),
      params: params
    });
    unansweredQuestionsList.value = response.data;

    console.log("Fetched Analysis Data:", conversationAnalysisData.value);

    if (conversationAnalysisData.value !== null && conversationAnalysisData.value !== undefined &&
        conversationAnalysisData.value.identifiedtags !== null && conversationAnalysisData.value.identifiedtags !== undefined &&
        Array.isArray(conversationAnalysisData.value.identifiedtags) === true && conversationAnalysisData.value.identifiedtags.length > 0) {
      try {
          const tagIds = conversationAnalysisData.value.identifiedtags;
          const tagsResponse = await axios.get(`${globalState.apiEndpoint}/api/tags/`, { 
              params: { ids: tagIds.join(',') }, 
              headers: getAuthHeaders() 
          });
          conversationTags.value = tagsResponse.data.map(tag => tag.tagname);
          console.log("Fetched Tag Names:", conversationTags.value);
      } catch (tagsError) {
          console.error('Failed to load tag names:', tagsError);
          toast.warning(t('unansweredQuestions.toasts.loadTagsError'));
          conversationTags.value = [];
      }
    } else {
        conversationTags.value = [];
    }
  } catch (error) {
    toast.error(t('unansweredQuestions.toasts.loadQuestionsError'));
    console.error('Error loading unanswered questions:', error);
    handleAuthError(error);
    unansweredQuestionsList.value = [];
  } finally {
    isLoading.value = false;
  }
};

const openAnswerModal = (item) => {
    selectedQuestion.value = item;
    newAnswer.value = ''; 
    isSubmitting.value = false;

    if (answerDialogRef.value !== null) {
      answerDialogRef.value.showModal();
    }
};

const closeAnswerModal = () => {
    if (answerDialogRef.value !== null) {
      answerDialogRef.value.close();
    }
    selectedQuestion.value = null;
    newAnswer.value = '';
};

const submitAnswer = async () => {
    if (selectedQuestion.value === null || selectedQuestion.value === undefined) {
        toast.error(t('unansweredQuestions.toasts.noQuestionSelected'));
        return;
    }
    if (newAnswer.value.trim() === '') {
        toast.error(t('unansweredQuestions.toasts.answerCannotBeEmpty'));
        return;
    }
    isSubmitting.value = true;
    try {
        const payload = {
            question: selectedQuestion.value.question,
            answer: newAnswer.value.trim(),
            agentId: Array.isArray(selectedQuestion.value.representativeAgentId) === true ? selectedQuestion.value.representativeAgentId[0] : selectedQuestion.value.representativeAgentId
        };
        
        if (payload.agentId === null || payload.agentId === undefined) {
             toast.error(t('unansweredQuestions.toasts.agentNotDetermined'));
             isSubmitting.value = false;
             return;
        }

        const response = await axios.post(`${globalState.apiEndpoint}/api/unanswered/addAnsweredPair`, payload, {
            headers: getAuthHeaders()
        });

        if (response.status === 200 || response.status === 207) {
             toast.success(isDefinedAndNotEmpty(response.data.message) != false ? response.data.message : t('unansweredQuestions.toasts.addAnswerSuccess'));
             closeAnswerModal();
            
             fetchUnansweredQuestions(); 
        } else {
             toast.error(isDefinedAndNotEmpty(response.data.message) != false ? response.data.message : t('unansweredQuestions.toasts.addAnswerFailure'));
        }

    } catch (error) {
        console.error('Error submitting answer:', error);
        const errorMessage = error?.response?.data?.message || t('unansweredQuestions.toasts.addAnswerToKBError');
        toast.error(errorMessage);
        handleAuthError(error);
    } finally {
        isSubmitting.value = false;
    }
};


const openConversationModal = async (item) => {
  if (item === null || item === undefined) {
    toast.error(t('unansweredQuestions.toasts.itemDataMissing'));
    return;
  }
  let sessionIdCandidate = normalizeId(item.representativeSessionId);
  if (sessionIdCandidate === null) sessionIdCandidate = normalizeId(item.sessionid);
  if (sessionIdCandidate === null) sessionIdCandidate = normalizeId(item.sessionId);
  let conversationIdCandidate = normalizeId(item.representativeConversationId);
  if (conversationIdCandidate === null) conversationIdCandidate = normalizeId(item.conversationid);
  if (conversationIdCandidate === null) conversationIdCandidate = normalizeId(item.conversationId);
  if (sessionIdCandidate === null) {
    toast.error(t('unansweredQuestions.toasts.sessionIdMissing'));
    return;
  }
  if (conversationIdCandidate === null) {
    toast.error(t('unansweredQuestions.toasts.conversationIdMissing'));
    return;
  }
  selectedSessionId.value = sessionIdCandidate;
  selectedConversationId.value = conversationIdCandidate;
  isLoadingConversation.value = true;
  conversationHistory.value = [];
  conversationAnalysisData.value = null;
  conversationTags.value = [];
  isConversationModalOpen.value = true;
  isLoadingConversation.value = true;
  try {
    const conversationResponse = await axios.get(`${globalState.apiEndpoint}/api/reports/conversation/${encodeURIComponent(selectedSessionId.value)}`, {
      headers: getAuthHeaders()
    });
    const conversationPayload = conversationResponse !== null && conversationResponse !== undefined && typeof conversationResponse.data === 'object' && conversationResponse.data !== null && Array.isArray(conversationResponse.data) !== true
      ? conversationResponse.data
      : {};
    conversationHistory.value = Array.isArray(conversationPayload.history) === true
      ? conversationPayload.history
      : (Array.isArray(conversationResponse.data) === true ? conversationResponse.data : []);
    conversationAnalysisData.value = conversationPayload.analysis !== undefined ? conversationPayload.analysis : null;
    if (conversationPayload.conversationId !== undefined && conversationPayload.conversationId !== null && String(conversationPayload.conversationId).trim() !== '') {
      selectedConversationId.value = String(conversationPayload.conversationId);
    }
    if (conversationAnalysisData.value !== null && conversationAnalysisData.value !== undefined &&
        conversationAnalysisData.value.identifiedtags !== null && conversationAnalysisData.value.identifiedtags !== undefined &&
        Array.isArray(conversationAnalysisData.value.identifiedtags) === true && conversationAnalysisData.value.identifiedtags.length > 0) {
        try {
            const tagIds = conversationAnalysisData.value.identifiedtags;
            const tagsResponse = await axios.get(`${globalState.apiEndpoint}/api/tags/`, { 
                params: { ids: tagIds.join(',') }, 
                headers: getAuthHeaders() 
            });
            conversationTags.value = tagsResponse.data.map(tag => tag.tagname);
        } catch (tagsError) {
            console.error('Failed to load tag names for convo modal:', tagsError);
            toast.warning(t('unansweredQuestions.toasts.loadTagsConvoError'));
            conversationTags.value = [];
        }
    } else {
        conversationTags.value = [];
    }
  } catch (error) {
    toast.error(t('unansweredQuestions.toasts.loadHistoryError'));
    console.error('Error loading conversation history:', error);
    handleAuthError(error);
  } finally {
    isLoadingConversation.value = false;
  }
};

const closeConversationModal = () => {
  isConversationModalOpen.value = false; 
  selectedSessionId.value = null;
  selectedConversationId.value = null;
  conversationHistory.value = [];
  conversationAnalysisData.value = null;
  conversationTags.value = [];
  isLoadingConversation.value = false; 
};

const exportConversationToPdf = async () => { 
  if (conversationHistory.value === undefined || conversationHistory.value.length === 0 || conversationContainerRef.value === undefined || conversationContainerRef.value === null) {
    toast.info(t('reports.toasts.noConversationDataToExport'));
    return;
  }
  const elementToCapture = conversationContainerRef.value;
  const originalBgColor = elementToCapture.style.backgroundColor; 
  elementToCapture.style.backgroundColor = '#FFFFFF'; 
  try {
    const canvas = await html2canvas(elementToCapture, { scale: 2, useCORS: true, logging: false, scrollX: 0, scrollY: -window.scrollY });
    const imgData = canvas.toDataURL('image/png');
    const doc = new jsPDF();
    const imgProps = doc.getImageProperties(imgData);
    const pdfWidth = doc.internal.pageSize.getWidth() - 20; 
    const pdfHeight = doc.internal.pageSize.getHeight() - 20; 
    const imgWidth = imgProps.width;
    const imgHeight = imgProps.height;
    let currentPosition = 10;
    let remainingImageHeight = imgHeight;
    let sourceY = 0;
    while (remainingImageHeight > 0) {
      const availableHeightOnPage = pdfHeight - currentPosition;
      const heightToDrawOnPage = Math.min(remainingImageHeight * (pdfWidth / imgWidth), availableHeightOnPage);
      const sourceHeight = heightToDrawOnPage / (pdfWidth / imgWidth);
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = imgWidth;
      tempCanvas.height = sourceHeight;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
      const pageImgData = tempCanvas.toDataURL('image/png');
      doc.addImage(pageImgData, 'PNG', 10, currentPosition, pdfWidth, heightToDrawOnPage);
      remainingImageHeight -= sourceHeight;
      sourceY += sourceHeight;
      if (remainingImageHeight > 0) {
        doc.addPage();
        currentPosition = 10; 
      } else {
        currentPosition += heightToDrawOnPage;
      }
    }
    const sessionId = String(isDefinedAndNotEmpty(selectedSessionId.value) != false ? selectedSessionId.value : 'conversation').replace(/[-_]/g, '');
    const dateStr = new Date().toISOString().slice(0, 10).replace(/[-_]/g, '');
    const fileName = `conversation${sessionId}${dateStr}.pdf`;
    doc.save(fileName);
    toast.success(t('reports.toasts.exportPdfSuccess'));
  } catch (error) {
    console.error('Error exporting conversation to PDF:', error);
    toast.error(t('reports.toasts.exportPdfError'));
  } finally {
    elementToCapture.style.backgroundColor = originalBgColor; 
  }
};

const exportConversationToTxt = () => {
  if (conversationHistory.value === undefined || conversationHistory.value.length === 0) {
    toast.info(t('reports.toasts.noConversationDataToExport'));
    return;
  }
  try {
    let textContent = `Conversation: ${selectedSessionId.value}\n\n`;
    conversationHistory.value.forEach(turn => {
      if (turn.userquery != undefined) {
        const timestamp = formatDateTimeForViewer(turn.createdat);
        textContent += `User (${timestamp}):\n${turn.userquery}\n\n`;
      }
      if (turn.response != undefined) {
        const timestamp = formatDateTimeForViewer(turn.createdat); 
        const agentResponseText = turn.response.replace(/<[^>]*>/g, ''); 
        textContent += `Agent (${timestamp}):\n${agentResponseText}\n\n`;
      }
    });
    const sessionId = isDefinedAndNotEmpty(selectedSessionId.value) != false ? selectedSessionId.value : 'conversation';
    const dateStr = new Date().toISOString().slice(0, 10);
    const fileName = `conversation_${sessionId}_${dateStr}.txt`;
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    toast.success(t('reports.toasts.exportTxtSuccess'));
  } catch (error) {
    console.error('Error exporting conversation to TXT:', error);
    toast.error(t('reports.toasts.exportTxtError'));
  }
};

const formatDateHardcoded = (dateValue) => {
  return formatDateTimeForViewer(dateValue);
};

function getTextDirection(text) {
  if (text === null) return 'ltr';
  if (text === undefined) return 'ltr';
  if (text.length === 0) return 'ltr';
  const firstChar = text.charCodeAt(0);

  const isRtlChar = (firstChar >= 0x0590 && firstChar <= 0x05FF) || (firstChar >= 0x0600 && firstChar <= 0x06FF);
  if (isRtlChar === true) {
    return 'rtl';
  } else {
    return 'ltr';
  }
}

function hasConversationText(text) {
  return typeof text === 'string' && text.trim().length > 0;
}

function escapeHtml(unsafe) {
  if (unsafe === null) return '';
  if (unsafe === undefined) return '';
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const getSentimentBadgeClass = (sentiment) => {
  if (sentiment === undefined) return 'badge-ghost';
  if (sentiment === null) return 'badge-ghost';
  const sentimentLower = sentiment.toLowerCase();
  if (sentimentLower.includes('very positive') === true) return 'badge-success';
  if (sentimentLower.includes('positive') === true) return 'badge-success badge-outline';
  if (sentimentLower.includes('very negative') === true) return 'badge-error';
  if (sentimentLower.includes('negative') === true) return 'badge-error badge-outline';
  if (sentimentLower.includes('mixed') === true) return 'badge-warning';
  return 'badge-neutral';
};

const formatDateStandard = (dateValue) => {
  if (dateValue === null) return '';
  if (dateValue === undefined) return '';
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime()) === true) return String(dateValue); 

    return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour:'2-digit', minute:'2-digit'});
  } catch (error) {
    console.error('Error formatting date standard:', error, dateValue);
    return String(dateValue); 
  }
};


const discardSelectedQuestions = async () => {
  if (selectedItems.value.length === 0) {
    toast.info(t('unansweredQuestions.toasts.noQuestionsToDiscard'));
    return;
  }
  isDiscarding.value = true;
  try {
    const questionsToDiscard = selectedItems.value.map(item => item.question);
    
    const response = await axios.post(`${globalState.apiEndpoint}/api/unanswered/discard`, 
      { questions: questionsToDiscard }, 
      {
        headers: getAuthHeaders()
      }
    );

    if (response.status === 200) {
      toast.success(t('unansweredQuestions.toasts.discardSuccess', { count: isDefinedAndNotEmpty(response.data.discardedCount) === true ? response.data.discardedCount : selectedItems.value.length }));
      selectedItems.value = []; 
      isDiscardModalOpen.value = false;
      await fetchUnansweredQuestions();
    } else {
      toast.error(isDefinedAndNotEmpty(response.data.message) != false ? response.data.message : t('unansweredQuestions.toasts.discardFailure'));
    }
  } catch (error) {
    console.error('Error discarding questions:', error);
    toast.error(isDefinedAndNotEmpty(error.response?.data?.message) != false ? error.response?.data?.message : t('unansweredQuestions.toasts.discardError'));
    handleAuthError(error);
  } finally {
    isDiscarding.value = false;
  }
};

const fetchAgentsForFilter = async () => {
  try {
    const data = await getAllAIChats({ includeStyles: false });
    availableAgentsForFilter.value = Array.isArray(data) === true ? data : [];
  } catch (error) {
    console.error('Error fetching agents for filter:', error);
    toast.error(t('unansweredQuestions.toasts.loadAgentsError'));
    handleAuthError(error);
  }
};

onMounted(() => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 7);
  dateRange.value = [startDate, endDate];

  fetchUnansweredQuestions();
  fetchAgentsForFilter();
});

function normalizeId(value) {
  if (value === undefined) return null;
  if (value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    return trimmed;
  }
  return value;
}

</script>

<style scoped>
:root {
    --fdaf7e9e: #e0e7ff !important;
}

.unanswered-container {
  max-width: 1600px;
  margin: 0 auto;
}


.results-card {

}
.easy-checkbox input[type=checkbox]:checked:before{
  background-color: var(--fdaf7e9e) !important;
}

.enterprise-table {
  @apply w-full;
  font-family: "Inter", sans-serif;
}

.enterprise-table :deep(th) {
  @apply bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-medium py-3 px-4 sticky top-0 z-10;
}

.enterprise-table :deep(td) {
  @apply py-3 px-4 border-t border-gray-200 dark:border-gray-700;
}

.enterprise-table :deep(tr:hover) {
  @apply bg-gray-50 dark:bg-gray-700;
}

.loading-spinner {
  @apply w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-shell .bg-base-300 {
  background: linear-gradient(90deg, rgba(148, 163, 184, 0.16) 0%, rgba(148, 163, 184, 0.32) 50%, rgba(148, 163, 184, 0.16) 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s linear infinite;
}

.chat-header { 
  box-shadow: none; 
  background-color: transparent; 
} 
.chat-bubble { 
  max-width: 80%; 
  padding: 0.75rem 1rem; 
  border-radius: 1rem; 
  line-height: 1.5; 
  word-wrap: break-word; 
  white-space: pre-wrap; 
} 
.chat-start .chat-bubble { 
  background: #cadff4; 
  color: #1e293b; 
  border-bottom-right-radius: 0.25rem; 
} 
.chat-end .chat-bubble { 
  background: #2563eb; 
  color: white; 
  border-bottom-left-radius: 0.25rem; 
} 
.chat-bubble :deep(p) { 
  margin: 0; 
  line-height: 1.5; 
} 
.chat-bubble :deep(a) { 
  color: inherit; 
  text-decoration: underline; 
} 
.chat-bubble::before{display:none;} 
.chat-bubble :deep(pre), .chat-bubble :deep(code) { 
  background-color: rgba(0,0,0,0.1); 
  padding: 0.1em 0.3em; 
  border-radius: 4px; 
  font-family: monospace; 
} 

.search-fields-grid {
  margin: -0.75rem;
}
.search-fields-grid > * {
  margin: 0.75rem;
}

.table-buttons-row {
  margin: -0.5rem;
}
.table-buttons-row > * {
  margin: 0.5rem;
}


.enterprise-table :deep(td:first-child input[type='checkbox']) {
  accent-color: #e0e7ff;
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
}

.enterprise-table :deep(th:first-child input[type='checkbox']) {
  accent-color: #e0e7ff; 
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
}

</style> 

<style>
html[dir="rtl"] .mr-1 {
  margin-left: 0.25rem;
  margin-right: 0;
}
html[dir="rtl"] .ml-1 {
  margin-right: 0.25rem;
  margin-left: 0;
}
html[dir="rtl"] .mr-2 {
  margin-left: 0.5rem;
  margin-right: 0;
}
html[dir="rtl"] .ml-2 {
  margin-right: 0.5rem;
  margin-left: 0;
}
html[dir="rtl"] .mr-3 {
  margin-left: 0.75rem;
  margin-right: 0;
}
html[dir="rtl"] .ml-3 {
  margin-right: 0.75rem;
  margin-left: 0;
}
</style> 